import { EmploymentList } from '../../src/features/users/EmploymentList'
/**
 * Profile (Мой профиль) — the signed-in employee's own card.
 *
 * Tamagui port of the handoff `ProfileScreen` (screen-misc.jsx), re-skinned to
 * the AS Life brand (navy cover + bronze bloom instead of the handoff purple):
 *  • cover banner with avatar, name/role and an Edit action
 *  • contact card (email / phone / office / department)
 *  • stats card (tasks · finished courses · vacation days) + skills chips
 *
 * Identity/contact come from live `/users/me`; the tasks stat from `/edo/tasks`,
 * finished courses from `/learning/me/summary`, vacation days from
 * `/users/me/balances`. Edit mode patches phone + skills via `PATCH /users/me`,
 * and the avatar tile uploads via `POST /users/me/avatar` (web file picker).
 * Any field the API does not return renders as "—" — no demo/placeholder data.
 */
import { useState } from 'react'
import { Platform } from 'react-native'
import { Input, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../src/i18n'
import { useResponsive } from '../../src/lib/responsive'
import { useMe } from '../../src/features/users/useUsers'
import { useMyTasks, useVacationBalance } from '../../src/features/edo/useEdo'
import { useBalances, useUpdateMe, useUploadAvatar } from '../../src/features/profile/useProfile'
import { useLearningSummary } from '../../src/features/learning/useLearning'
import { pickFileWeb } from '../../src/lib/files'
import { fmtNum } from '../../src/lib/format'
import { palette } from '../../src/theme/palette'
import { Avatar, Button, Card, Enter, Icon, QueryState, Tag } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

const TERTIARY = '$tertiary'

/** One label/value contact row with a leading icon and bottom divider. */
function ContactRow({
  icon,
  label,
  value,
  last,
  children,
}: {
  icon: string
  label: string
  value?: string
  last?: boolean
  children?: React.ReactNode
}) {
  return (
    <XStack
      gap={12}
      paddingVertical={12}
      borderBottomWidth={last ? 0 : 1}
      borderColor="$borderSecondary"
      alignItems="center"
    >
      <Icon name={icon} size={16} color={TERTIARY} />
      <YStack flex={1}>
        <Text fontSize={11} color={TERTIARY}>
          {label}
        </Text>
        {children ?? (
          <Text fontSize={13} color="$color" marginTop={1}>
            {value}
          </Text>
        )}
      </YStack>
    </XStack>
  )
}

function Stat({ value, label }: { value: number | string; label: string }) {
  return (
    <YStack alignItems="center" flex={1}>
      <Text fontSize={22} fontWeight="700" color="$color">
        {value}
      </Text>
      <Text fontSize={11} color={TERTIARY}>
        {label}
      </Text>
    </YStack>
  )
}

export default function ProfileScreen() {
  const { l } = useApp()
  const { isPhone } = useResponsive()

  const meQuery = useMe()
  const { data: me } = meQuery
  const { data: liveTasks } = useMyTasks()
  const { data: balances } = useBalances()
  const { data: vacBalanceLive } = useVacationBalance()
  const { data: learning } = useLearningSummary()
  const uploadAvatar = useUploadAvatar()

  const [editing, setEditing] = useState(false)
  const [phoneDraft, setPhoneDraft] = useState('')
  const [skillsDraft, setSkillsDraft] = useState<string[]>([])
  const [newSkill, setNewSkill] = useState('')
  const updateMe = useUpdateMe(() => setEditing(false))

  // Live /users/me only — no invented data; any missing field renders as "—".
  const DASH = '—'
  const name = me?.fullName || DASH
  const role = me?.jobTitle || DASH
  const dept = me?.department || DASH
  const email = me?.email || DASH
  const phone = me?.phone || DASH
  const office = [me?.location?.city, me?.location?.name].filter(Boolean).join(' · ') || DASH
  const skills = me?.skills ?? []

  // Отпускные дни — приоритет живому остатку из 1С (GET /edo/vacation/balance);
  // фолбэк на старый /users/me/balances, затем «—». Целое как есть, дробь — до 2 знаков.
  const liveVacDays = vacBalanceLive?.source === 'zup' ? vacBalanceLive.balanceDays : null
  const vacBalance = balances?.items.find((b) => b.kind === 'vacation')
  const rawVacDays = liveVacDays ?? vacBalance?.availableDays ?? vacBalance?.totalDays ?? null
  const vacationDays = rawVacDays == null ? DASH : fmtNum(rawVacDays, 2)
  const coursesDone = learning?.completed ?? DASH
  const taskCount = Array.isArray(liveTasks) ? liveTasks.length : DASH

  const startEdit = () => {
    setPhoneDraft(me?.phone ?? '')
    setSkillsDraft(me?.skills?.length ? me.skills : [])
    setEditing(true)
  }
  const save = () => {
    if (updateMe.isPending) return
    updateMe.mutate({ phone: phoneDraft.trim() || undefined, skills: skillsDraft })
  }
  const addSkill = () => {
    const s = newSkill.trim()
    if (!s || skillsDraft.includes(s)) return
    setSkillsDraft((p) => [...p, s])
    setNewSkill('')
  }
  const removeSkill = (s: string) => setSkillsDraft((p) => p.filter((x) => x !== s))

  // Avatar upload — web file picker (native needs an image picker; follow-up).
  const pickAvatar = async () => {
    const file = await pickFileWeb('image/*')
    if (!file) return
    const form = new FormData()
    form.append('file', file)
    uploadAvatar.mutate(form)
  }
  const avatarPressable = editing && Platform.OS === 'web'

  return (
    <Page maxWidth={960}>
      {/* useMe — главный запрос страницы: остальные 4 (tasks/balances/vac/learning)
          деградируют в «—» как раньше, их отдельно не оборачиваем. */}
      <QueryState q={meQuery}>
      {/* ── Header / cover ─────────────────────────────────────────── */}
      <Enter index={0}>
      <Card overflow="hidden" padding={0}>
        {/* navy cover with a soft bronze + lighter-navy bloom */}
        <Stack height={120} backgroundColor={palette.coverNavy} overflow="hidden">
          <Stack
            position="absolute"
            top={-70}
            right={-30}
            width={220}
            height={220}
            borderRadius={999}
            backgroundColor={palette.navy}
            opacity={0.55}
          />
          <Stack
            position="absolute"
            top={20}
            right={70}
            width={120}
            height={120}
            borderRadius={999}
            backgroundColor={palette.bronzeBrushed}
            opacity={0.14}
          />
          {/* brushed-bronze bottom edge — brand motif from the login screen */}
          <Stack position="absolute" bottom={0} left={0} right={0} height={3} backgroundColor={palette.bronzeBrushed} opacity={0.85} />
        </Stack>

        <XStack
          paddingHorizontal={24}
          paddingBottom={24}
          marginTop={-40}
          gap={16}
          alignItems="flex-end"
          flexWrap="wrap"
        >
          <Stack
            padding={3}
            borderRadius={999}
            backgroundColor="$background"
            onPress={avatarPressable ? pickAvatar : undefined}
            cursor={avatarPressable ? 'pointer' : 'default'}
            animation="quicker"
            hoverStyle={avatarPressable ? { scale: 1.04 } : undefined}
            pressStyle={avatarPressable ? { scale: 0.96 } : undefined}
          >
            <Avatar name={name} seed="me" size={84} uri={me?.avatarUrl} />
            {avatarPressable && (
              <Stack
                position="absolute"
                bottom={3}
                right={3}
                width={28}
                height={28}
                borderRadius={999}
                backgroundColor={palette.navy}
                borderWidth={2}
                borderColor="$background"
                alignItems="center"
                justifyContent="center"
              >
                <Icon name="camera" size={14} color="#fff" />
              </Stack>
            )}
          </Stack>
          <YStack flex={1} minWidth={200} paddingBottom={4}>
            <Text fontSize={20} fontWeight="800" color="$color" letterSpacing={-0.3}>
              {name}
            </Text>
            <Text fontSize={13} color="$muted">
              {role} · {dept}
            </Text>
          </YStack>
          {editing ? (
            <XStack gap={8}>
              <Button onPress={() => setEditing(false)}>{l('c.cancel')}</Button>
              <Button variant="primary" onPress={save} opacity={updateMe.isPending ? 0.6 : 1}>
                {l('c.save')}
              </Button>
            </XStack>
          ) : (
            <Button icon={<Icon name="pencil" size={15} color="$primary" />} onPress={startEdit}>
              {l('profile.editProfile')}
            </Button>
          )}
        </XStack>
      </Card>
      </Enter>

      {/* ── Detail grid ────────────────────────────────────────────── */}
      <XStack gap={16} flexDirection={isPhone ? 'column' : 'row'} alignItems="flex-start">
        {/* contact */}
        <Enter index={1} flex={isPhone ? undefined : 1} width={isPhone ? '100%' : undefined}>
        <Card padding={20}>
          <Text fontSize={13} fontWeight="700" color="$color" marginBottom={8}>
            {l('profile.contact')}
          </Text>
          <ContactRow icon="mail" label={l('profile.email')} value={email} />
          <ContactRow icon="phone" label={l('profile.phone')}>
            {editing ? (
              <Input
                value={phoneDraft}
                onChangeText={setPhoneDraft}
                placeholder={l('profile.phone')}
                placeholderTextColor="$tertiary"
                height={32}
                marginTop={2}
                fontSize={13}
                color="$color"
                backgroundColor="$bgLayout"
                borderWidth={1}
                borderColor="$borderColor"
                borderRadius={6}
                paddingHorizontal={9}
                focusStyle={{ borderColor: '$primary' }}
              />
            ) : (
              <Text fontSize={13} color="$color" marginTop={1}>
                {phone}
              </Text>
            )}
          </ContactRow>
          <ContactRow icon="map-pin" label={l('profile.office')} value={office} />
          <ContactRow icon="building-2" label={l('profile.dept')} value={dept} last />
          <EmploymentList jobs={me?.employments} conflict={me?.employmentSelectionRequired} />
        </Card>
        </Enter>

        {/* stats + skills */}
        <Enter index={2} width={isPhone ? '100%' : 300}>
        <YStack gap={16}>
          <Card padding={20}>
            <XStack justifyContent="space-around">
              <Stat value={taskCount} label={l('profile.statTasks')} />
              <Stat value={coursesDone} label={l('profile.statCourses')} />
              <Stat value={vacationDays} label={l('profile.statDays')} />
            </XStack>
          </Card>
          <Card padding={20}>
            <Text fontSize={13} fontWeight="700" color="$color" marginBottom={12}>
              {l('profile.skills')}
            </Text>
            <XStack flexWrap="wrap" gap={6}>
              {!editing && skills.length === 0 && (
                <Text fontSize={13} color={TERTIARY}>
                  —
                </Text>
              )}
              {(editing ? skillsDraft : skills).map((s) =>
                editing ? (
                  <XStack
                    key={s}
                    alignItems="center"
                    gap={5}
                    backgroundColor={palette.navySoft}
                    borderRadius={999}
                    paddingLeft={10}
                    paddingRight={6}
                    paddingVertical={3}
                  >
                    <Text fontSize={12} fontWeight="600" color="$primary">
                      {s}
                    </Text>
                    <Stack onPress={() => removeSkill(s)} cursor="pointer" hoverStyle={{ opacity: 0.6 }}>
                      <Icon name="x" size={13} color="$primary" />
                    </Stack>
                  </XStack>
                ) : (
                  <Tag key={s} tone="purple">
                    {s}
                  </Tag>
                ),
              )}
            </XStack>
            {editing && (
              <XStack gap={8} marginTop={12} alignItems="center">
                <Input
                  flex={1}
                  value={newSkill}
                  onChangeText={setNewSkill}
                  onSubmitEditing={addSkill}
                  placeholder={l('profile.addSkill')}
                  placeholderTextColor="$tertiary"
                  height={34}
                  fontSize={13}
                  color="$color"
                  backgroundColor="$bgLayout"
                  borderWidth={1}
                  borderColor="$borderColor"
                  borderRadius={6}
                  paddingHorizontal={9}
                  focusStyle={{ borderColor: '$primary' }}
                />
                <Button size="sm" onPress={addSkill} icon={<Icon name="plus" size={14} color="$primary" />} />
              </XStack>
            )}
          </Card>
        </YStack>
        </Enter>
      </XStack>

      <Stack height={8} />
      </QueryState>
    </Page>
  )
}
